import { Incident, IncidentType, Severity } from '../types/domain';

const labels: Record<IncidentType, string> = {
  face_missing: 'Face missing',
  looking_away: 'Looking away',
  head_down: 'Head down',
  head_left: 'Head turned left',
  head_right: 'Head turned right',
  eyes_closed: 'Eyes closed',
  multiple_faces: 'Multiple faces',
  phone_detected: 'Phone detected',
  book_detected: 'Book detected',
  laptop_detected: 'Laptop detected',
  person_entered: 'Additional person',
  left_seat: 'Candidate left seat',
  too_close: 'Candidate too close',
  too_far: 'Candidate too far',
  low_light: 'Low lighting',
  camera_covered: 'Camera covered',
  talking: 'Talking',
  whispering: 'Whispering',
  loud_noise: 'Loud sound',
  television: 'Television-like sound',
  music: 'Music-like sound',
  background_conversation: 'Background conversation',
  window_blur: 'Window blur',
  tab_switch: 'Tab switch',
  fullscreen_exit: 'Fullscreen exit',
  attempted_zoom: 'Attempted zoom',
  resize: 'Window resize',
  clipboard_copy: 'Clipboard copy',
  clipboard_paste: 'Clipboard paste',
  right_click: 'Right click',
  keyboard_shortcut: 'Keyboard shortcut'
};

interface IncidentTiming {
  timestamp?: string;
  startedAt?: string;
  endedAt?: string;
  durationSeconds?: number;
}

export function calculateDurationSeconds(startedAt: string, endedAt: string): number {
  return Math.max(0, Math.round((new Date(endedAt).getTime() - new Date(startedAt).getTime()) / 1000));
}

export function makeIncident(
  type: IncidentType,
  confidence: number,
  severity: Severity,
  elapsedSeconds: number,
  source: Incident['source'],
  screenshot?: string,
  timing: IncidentTiming = {}
): Incident {
  const timestamp = timing.timestamp ?? new Date().toISOString();
  const startedAt = timing.startedAt ?? timestamp;
  const endedAt = timing.endedAt;
  return {
    id: crypto.randomUUID(),
    type,
    label: labels[type],
    severity,
    confidence: Math.round(confidence),
    timestamp,
    elapsedSeconds,
    startedAt,
    endedAt,
    durationSeconds: timing.durationSeconds ?? (endedAt ? calculateDurationSeconds(startedAt, endedAt) : undefined),
    source,
    screenshot
  };
}

export function closeIncidentDuration(incident: Incident, endedAt: string): Incident {
  const startedAt = incident.startedAt ?? incident.timestamp;
  return {
    ...incident,
    startedAt,
    endedAt,
    durationSeconds: calculateDurationSeconds(startedAt, endedAt)
  };
}

export function severityColor(severity: Severity): string {
  return {
    low: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-950/50',
    medium: 'text-amber-600 bg-amber-50 dark:bg-amber-950/50',
    high: 'text-orange-600 bg-orange-50 dark:bg-orange-950/50',
    critical: 'text-red-600 bg-red-50 dark:bg-red-950/50'
  }[severity];
}
